/**
 * SDK State Management
 *
 * State machine:
 * - unloaded: SDK loaded but not initialized
 * - loading: iframe being created and loading
 * - ready: iframe loaded, waiting for open()
 * - open: modal visible to user
 */

export type SdkState = "unloaded" | "loading" | "ready" | "open";

let currentState: SdkState = "unloaded";

/**
 * Valid state transitions
 */
const validTransitions: Record<SdkState, SdkState[]> = {
  unloaded: ["loading"],
  loading: ["ready", "unloaded"], // unloaded on error
  ready: ["open"],
  open: ["ready"], // close returns to ready, not unloaded
};

/**
 * Gets the current SDK state
 */
export function getState(): SdkState {
  return currentState;
}

/**
 * Sets the SDK state with validation
 * @returns true if transition was valid, false otherwise
 */
export function setState(newState: SdkState): boolean {
  if (!validTransitions[currentState].includes(newState)) {
    console.warn(
      `[NbxHub] Invalid state transition: ${currentState} -> ${newState}`,
    );
    return false;
  }

  currentState = newState;
  return true;
}

/**
 * Resets state to unloaded (for cleanup/testing)
 */
export function resetState(): void {
  currentState = "unloaded";
}

/**
 * Checks if SDK is in a specific state
 */
export function isState(state: SdkState): boolean {
  return currentState === state;
}
